import {Alert, Button, Checkbox, message, Select, Space, Table, Upload} from 'antd';
import {useState} from 'react';
import {useQueryClient} from 'react-query';
import api from '../../api';

const {Dragger} = Upload;

const fieldOrder = [
  'greeting',
  'title',
  'first_name',
  'last_name',
  'grad',
  'company',
  'address',
  'post_index',
  'area',
  'state',
  'phone',
  'email',
  'status',
];

const defaultFieldLabels = {
  greeting: 'Anrede',
  title: 'Titel',
  first_name: 'Vorname',
  last_name: 'Nachname',
  grad: 'Grad',
  company: 'Firma',
  address: 'Straße, Nr.',
  post_index: 'PLZ',
  area: 'Ort',
  state: 'Bundesland',
  phone: 'Telefon',
  email: 'E-Mail',
  status: 'Teilnehmerstatus',
};

const makeFormData = ({file, mapping, hasHeader}) => {
  const data = new FormData();
  data.append('file', file);
  if (mapping) {
    data.append('mapping', JSON.stringify(mapping));
  }
  if (hasHeader !== undefined) {
    data.append('has_header', hasHeader ? 'true' : 'false');
  }
  return data;
};

const SummaryItem = ({label, value}) => <span style={{marginRight: 18}}>
  {label}: <b>{value || 0}</b>
</span>;

const InvitationUpload = ({...props}) => {
  const [file, setFile] = useState();
  const [analysis, setAnalysis] = useState();
  const [mapping, setMapping] = useState({});
  const [hasHeader, setHasHeader] = useState(false);
  const [preview, setPreview] = useState();
  const [result, setResult] = useState();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const resetImportState = () => {
    setAnalysis(undefined);
    setMapping({});
    setHasHeader(false);
    setPreview(undefined);
    setResult(undefined);
  };

  const analyzeFile = async uploadFile => {
    setLoading(true);
    try {
      const {data} = await api.invitation.invitationUploadAnalyze({
        meetingId: props.meeting,
        data: makeFormData({file: uploadFile}),
      });
      setAnalysis(data);
      setMapping(data.mapping || {});
      setHasHeader(!!data.has_header);
      setPreview(undefined);
      setResult(undefined);
      message.success('Datei wurde analysiert.');
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Datei konnte nicht analysiert werden.');
    } finally {
      setLoading(false);
    }
  };

  const beforeUpload = uploadFile => {
    setFile(uploadFile);
    resetImportState();
    analyzeFile(uploadFile);
    return false;
  };

  const updateMapping = field => value => {
    setMapping(current => ({...current, [field]: value}));
    setPreview(undefined);
    setResult(undefined);
  };

  const createPreview = async () => {
    if (!file) {
      message.warning('Bitte zuerst eine Datei auswählen.');
      return;
    }
    setLoading(true);
    try {
      const {data} = await api.invitation.invitationUploadPreview({
        meetingId: props.meeting,
        data: makeFormData({file, mapping, hasHeader}),
      });
      setPreview(data);
      setResult(undefined);
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Vorschau konnte nicht erstellt werden.');
    } finally {
      setLoading(false);
    }
  };

  const confirmImport = async () => {
    if (!preview) {
      message.warning('Bitte zuerst eine Vorschau erstellen.');
      return;
    }
    setLoading(true);
    try {
      const {data} = await api.invitation.invitationUploadConfirm({
        meetingId: props.meeting,
        data: makeFormData({file, mapping, hasHeader}),
      });
      setResult(data);
      message.success('Import abgeschlossen.');
      try {
        await queryClient.invalidateQueries('invitation-list');
      } catch (refreshError) {
        message.warning('Import abgeschlossen, Liste konnte nicht aktualisiert werden.');
      }
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Import konnte nicht abgeschlossen werden.');
    } finally {
      setLoading(false);
    }
  };

  const fieldLabels = analysis?.field_labels || defaultFieldLabels;
  const columns = analysis?.columns || [];
  const previewRows = preview?.rows || [];
  const errorRows = previewRows.filter(row => row.errors?.length);

  const previewColumns = [
    {
      title: 'Reihe',
      dataIndex: 'row_number',
      key: 'row_number',
      width: 80,
    },
    {
      title: 'Name',
      key: 'name',
      render: row => `${row.values?.first_name || ''} ${row.values?.last_name || ''}`.trim(),
    },
    {
      title: 'E-Mail',
      key: 'email',
      render: row => row.values?.email,
    },
    {
      title: 'Teilnehmerstatus',
      key: 'status',
      render: row => row.values?.status,
    },
    {
      title: 'Ergebnis',
      key: 'result',
      render: row => row.errors?.length ? row.errors.join('; ') : row.action === 'update' ? 'wird aktualisiert' : 'neu',
    },
  ];

  return <div>
    <h3 className="title">Listenimport</h3>
    <hr className="hr"/>

    <Dragger
      accept=".csv,text/csv,text/plain"
      beforeUpload={beforeUpload}
      onRemove={() => {
        setFile(undefined);
        resetImportState();
      }}
      maxCount={1}
      multiple={false}>
      <p>CSV-Datei auswählen oder hier ablegen</p>
      <p>Semikolon und Komma werden automatisch erkannt.</p>
    </Dragger>

    {analysis && <div style={{marginTop: 20}}>
      <Alert
        type="info"
        showIcon
        message={`Erkannt: ${analysis.row_count} Datensätze, Trennzeichen "${analysis.delimiter}", Encoding ${analysis.encoding}`}
        style={{marginBottom: 20}}
      />

      <Checkbox
        checked={hasHeader}
        onChange={event => {
          setHasHeader(event.target.checked);
          setPreview(undefined);
          setResult(undefined);
        }}>
        Erste Zeile enthält Spaltenüberschriften
      </Checkbox>

      <h4 style={{marginTop: 20}}>Spalten zuordnen</h4>
      <Space direction="vertical" style={{width: '100%'}}>
        {fieldOrder.map(field => <div key={field} style={{display: 'flex', alignItems: 'center', gap: 12}}>
          <div style={{width: 180}}>{fieldLabels[field]}</div>
          <Select
            allowClear
            value={mapping[field]}
            onChange={updateMapping(field)}
            style={{width: '100%'}}
            placeholder="Spalte auswählen">
            {columns.map(column => <Select.Option key={column.index} value={column.index}>
              {column.name}
            </Select.Option>)}
          </Select>
        </div>)}
      </Space>

      {analysis.statuses?.length > 0 && <div style={{marginTop: 20}}>
        <b>Vorhandene Teilnehmerstatus:</b> {analysis.statuses.map(item => item.name).join(', ')}
      </div>}

      <Space style={{marginTop: 20}}>
        <Button onClick={createPreview} loading={loading}>
          Vorschau erstellen
        </Button>
        <Button type="primary" onClick={confirmImport} disabled={!preview} loading={loading}>
          Import bestätigen
        </Button>
      </Space>
    </div>}

    {preview && <div style={{marginTop: 20}}>
      <hr className="hr"/>
      <h4>Vorschau</h4>
      <p>
        <SummaryItem label="Datensätze" value={preview.summary?.total}/>
        <SummaryItem label="Neu" value={preview.summary?.new}/>
        <SummaryItem label="Wird aktualisiert" value={preview.summary?.updated}/>
        <SummaryItem label="Dubletten" value={preview.summary?.duplicates}/>
        <SummaryItem label="Unklar / Fehler" value={preview.summary?.errors}/>
      </p>
      {errorRows.length > 0 && <Alert
        type="warning"
        showIcon
        message={`${errorRows.length} Zeile(n) enthalten Fehler und werden beim Import übersprungen.`}
        style={{marginBottom: 12}}
      />}
      <Table
        columns={previewColumns}
        dataSource={previewRows}
        rowKey="row_number"
        size="small"
        pagination={{pageSize: 8}}
      />
    </div>}

    {result && <div style={{marginTop: 20}}>
      <hr className="hr"/>
      <h4>Import-Ergebnis</h4>
      <p>
        <SummaryItem label="Eingefügt" value={result.inserted}/>
        <SummaryItem label="Aktualisiert" value={result.updated}/>
        <SummaryItem label="Übersprungen" value={result.skipped}/>
        <SummaryItem label="Fehlerhaft" value={result.errors}/>
      </p>
    </div>}
  </div>;
}

export default InvitationUpload;
